import {
	auth_locked_devices,
	auth_unlocked_at,
	device_auths,
} from 'src/stores/device-runtime/local-auth';
import { updating_devices, rebooting_devices, firmwares_gen_2 } from 'src/stores/device-runtime/process';
import { devices_status, devices_settings } from 'src/stores/device-runtime/status';
import { devices } from 'src/stores/lists/devices';
import { syncAuthLockedFromAuthEnabled } from './auth_locked_helpers';

import {
	cloud_disabled_devices,
	authLockedDeviceModal,
	unauthorisedRequests,
} from 'src/stores/device-runtime/firmware-ui';

import { pending_connection_devices } from 'src/stores/device-runtime/firmware-ui';
import { get, writable } from 'svelte/store';

import { real_id, is_gen_2 } from './interface_helpers';
import { get_fresh_verified_local_ip, refresh_verified_local_ip } from './local_ip_probe_helpers';
import { checkMode } from './mode_switch_helpers';
import {
	get_local_settings,
	get_local_status,
	get_local_device_info,
	trigger401ToGetAuthHeader,
} from '../services/local_requests';
import { removeUpdatingState } from './firmware_helpers';
import { mergePartialDeviceStatus } from './operational_counters_helpers';

const cloud_disabled_status_cache = writable(
	JSON.parse(localStorage.getItem('cloud_disabled_status_cache')) || {},
);
cloud_disabled_status_cache.subscribe((val) =>
	localStorage.setItem('cloud_disabled_status_cache', JSON.stringify(val)),
);

const cloud_disabled_settings_cache = writable(
	JSON.parse(localStorage.getItem('cloud_disabled_settings_cache')) || {},
);
cloud_disabled_settings_cache.subscribe((val) =>
	localStorage.setItem('cloud_disabled_settings_cache', JSON.stringify(val)),
);

export const cloud_disabled_retries = writable({});

const RETRY_MAX = 3;
/** Increment for retry counter when local status fetch fails (non-401, non-success) */
const RETRY_INCREMENT = 1;

const STATUS_CACHE_TTL_MS = 5 * 60 * 1000;

const applyCloudDisabledStatus = (deviceId: string, statusPayload: any) => {
	statusPayload.online = true;
	statusPayload._updated = +(Date.now() / 1000).toFixed(0);

	const newStatus = mergePartialDeviceStatus(devices_status.getByRealId(deviceId), statusPayload);
	devices_status.update_device_status({ id: deviceId, msg: newStatus });

	const realId = real_id(deviceId);
	cloud_disabled_status_cache.update((cache) => ({
		...cache,
		[realId]: { status: newStatus, cachedAt: Date.now() },
	}));
};

const hydrateCloudDisabledStatusFromCache = (deviceId: string) => {
	const realId = real_id(deviceId);
	const entry = get(cloud_disabled_status_cache)[realId];
	if (!entry?.status || Date.now() - entry.cachedAt > STATUS_CACHE_TTL_MS) return;

	applyCloudDisabledStatus(deviceId, { ...entry.status, online: true });
};

export async function get_cloud_disabled() {
	const _cloud_disabled_devices = get(cloud_disabled_devices);

	for (const k in _cloud_disabled_devices) {
		const device = _cloud_disabled_devices[k];

		if (typeof get(cloud_disabled_retries)[device.id] === 'undefined') {
			cloud_disabled_retries.update((list) => {
				return { [device.id]: 0, ...list };
			});
		}

		// retry to get status/settings
		if (get(cloud_disabled_retries)[device.id] < RETRY_MAX) {
			const realId = real_id(device.id);
			hydrateCloudDisabledStatusFromCache(device.id);

			const localIp = get_fresh_verified_local_ip(realId) || device.ip;
			const status_req = await get_local_status(device, localIp);

			if (!status_req.isok) {
				void refresh_verified_local_ip(device.id, { debounced: true });
			}

			// console.log(d.ip, 'status is', status_req);
			if (get(cloud_disabled_retries)[device.id] > 0) {
				console.log('RETRY ', get(cloud_disabled_retries)[device.id], 'FOR', device.id);
			}
			// after getting the status we can handle the device state lists (updateing, rebooting etc.)
			if (status_req.isok) {
				// think of making updating_devices a derived store
				rebooting_devices.remove(device.id);

				if (get(pending_connection_devices).indexOf(device.id) > -1) {
					pending_connection_devices.remove(device.id);
				}

				// this check handles both gen 1 and 2
				if (typeof get(updating_devices)[device.id] !== 'undefined') {
					if (
						(!is_gen_2(device) && status_req?.update?.status !== 'updating') ||
						(is_gen_2(device) && typeof status_req?.sys?.available_updates.stable) ===
							'undefined'
					) {
						console.log('got status will remove from updating devices list');
						// updating_devices.remove(d.id);
						removeUpdatingState(get(devices)[device.id]);
						firmwares_gen_2.remove(device.id);

						applyCloudDisabledStatus(device.id, status_req);
						void update_settings(device);
					}
					return;
				}

				if (status_req?.update?.status === 'updating') {
					updating_devices.update(real_id(device.id), { fw: '', ts: Date.now() });
					devices_status.remove(device.id);
					devices_settings.remove(device.id);
				} else {
					applyCloudDisabledStatus(device.id, status_req);
					void update_settings(device);
				}

				cloud_disabled_retries.update((list) => {
					return { [device.id]: 0, ...list };
				});
			} else if (status_req.status == 401) {
				console.error('cloud disabled unauthorised');
				cloud_disabled_retries.update((list) => {
					return { [device.id]: RETRY_MAX, ...list };
				});
			} else {
				cloud_disabled_retries.update((list) => ({
					...list,
					[device.id]: (list[device.id] ?? 0) + RETRY_INCREMENT,
				}));
				console.log('get(cloud_disabled_retries)', get(cloud_disabled_retries));
			}
		} else {
			let _status = devices_status.getByRealId(device.id);
			console.log('max local retries exceeded,', _status);
			if (_status) {
				_status.online = false;
				devices_status.update_device_status({ id: device.id, msg: _status });
			}
		}
	}
}

export async function update_settings(d) {
	const settings_req = await get_local_settings(d, d.ip);
	if (settings_req.isok) {
		checkMode(d.id, settings_req);
		let newSettings = {
			...devices_settings.getByRealId(d.id),
			...settings_req,
		};
		devices_settings.update_devices_settings(d.id, newSettings);

		const deviceInfoRes = await get_local_device_info(d, d.ip);
		const id = real_id(d.id);
		if (deviceInfoRes?.isok) {
			const authEn = (deviceInfoRes as any)?.auth_en;
			if (authEn === false) {
				syncAuthLockedFromAuthEnabled(id, false);
			}
			if (get(device_auths)[id]) {
				auth_locked_devices.remove(id);
			}
		} else if (
			!deviceInfoRes?.isok &&
			deviceInfoRes?.status === 401 &&
			!(get(device_auths)[id] != null)
		) {
			auth_locked_devices.add(id);
		}

		auth_unlocked_at.recheckExpiredAndReLock();
	}
}

// same as clearing a device state and setting it as offline (statsu === undefined)
export async function clear_cloud_disabled(id) {
	devices_status.remove(id);
	devices_settings.remove(id);
}
